import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '@/lib/utils/date';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Skeleton } from '@/components/ui/skeleton';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { StatusFilterPills } from '@/components/common/StatusFilterPills';
import type { Device, DeviceFilters, DeviceStatus } from '../types';

interface DevicesDataTableProps {
  data: Device[];
  columns: ColumnDef<Device>[];
  isLoading: boolean;
  filters: DeviceFilters;
  updateFilter: <K extends keyof DeviceFilters>(key: K, value: DeviceFilters[K]) => void;
  updateFilters?: (updates: Partial<DeviceFilters>) => void;
  onClearFilters?: () => void;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

const DEVICE_STATUSES: DeviceStatus[] = ['ACTIVE', 'BLOCKED'];

const DEVICE_STATUS_ACTIVE: Record<DeviceStatus, string> = {
  ACTIVE: 'data-[state=on]:bg-emerald-500',
  BLOCKED: 'data-[state=on]:bg-red-500',
};

export function DevicesDataTable({
  data,
  columns,
  isLoading,
  filters,
  updateFilter,
  updateFilters,
  onClearFilters,
  controlsContainer,
  tableZoom: tableZoomProp,
}: DevicesDataTableProps) {
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('devices');
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  const {
    'createdAt.min': minDate,
    'createdAt.max': maxDate,
    status,
  } = filters;

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters['createdAt.min'] || filters['createdAt.max']) count++;
    if (filters.userId) count++;
    if (filters.status) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* Toolbar: status pills + table controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-2">
        <StatusFilterPills
          value={status}
          onChange={(v) => updateFilter('status', v as DeviceStatus | undefined)}
          options={DEVICE_STATUSES.map((s) => ({
            value: s,
            label: t(`status.${s}`),
            activeClassName: DEVICE_STATUS_ACTIVE[s],
          }))}
        />
        <div className="flex items-center gap-2">
          {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
        </div>
      </div>

      {/* Filters panel */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        gridCols={3}
      >
        {/* Created Date Range */}
        <FilterField label={t('createdDateRange')}>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between font-normal h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
              >
                <span className="truncate">
                  {minDate && maxDate
                    ? `${format(new Date(minDate), 'MM/dd/yy', { locale: dateFnsLocale })} - ${format(new Date(maxDate), 'MM/dd/yy', { locale: dateFnsLocale })}`
                    : t('all')}
                </span>
                <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar
                mode="range"
                selected={{
                  from: minDate ? new Date(minDate) : undefined,
                  to: maxDate ? new Date(maxDate) : undefined,
                }}
                onSelect={(range) => {
                  if (updateFilters) {
                    updateFilters({
                      'createdAt.min': range?.from?.toISOString(),
                      'createdAt.max': range?.to?.toISOString(),
                    });
                  } else {
                    updateFilter('createdAt.min', range?.from?.toISOString());
                    updateFilter('createdAt.max', range?.to?.toISOString());
                  }
                }}
                numberOfMonths={2}
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </FilterField>

        {/* User */}
        <FilterField label={t('user')}>
          <UniversalCombobox
            url="/users"
            listUrl="/users/select-box"
            labelKey="name"
            valueKey="id"
            selectedId={filters.userId}
            onSelectItem={(user) =>
              updateFilter('userId', user ? (user['id'] as number) : undefined)
            }
            placeholder={t('all')}
            className="w-full"
          />
        </FilterField>

      </CollapsibleFilters>

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="hover:bg-[#f8fafc]">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('noDevicesFound')}</h3>
                    <p className="text-[#64748b]">{t('noDevicesMessage')}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
